import { Box, IconButton } from "@bcc-ui/ui-components";
import classNames from "classnames";
import { useFeature } from "flagged";
import { t } from "i18next";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { AssetsSubmenu } from "./contents/assets-sub-menu/AssetsSubMenu";
import { ConnectionsSubmenu } from "./contents/ConnectionsSubMenu";
import { DashboardSubmenu } from "./contents/DashboardSubMenu";
import { HomeSubmenu } from "./contents/HomeSubMenu";
import NavigationSubMenu from "./contents/navigation-sub-menu/NavigationSubMenu";
import { ProductsSubmenu } from "./contents/ProductsSubMenu";
import { ReportsSubmenu } from "./contents/reports-sub-menu/ReportsSubMenu";
import { SettingsSubmenu } from "./contents/settings-sub-menu/SettingsSubMenu";
import { SupportSubmenu } from "./contents/SupportSubMenu";
import style from "./sub-navigation-panel.scss";
import { NavigationHeader, NavigationHeaderBadge } from "./ui-components/navigation-header/NavigationHeader";
import { useAIPanelContext } from "components/ai-panel/AIPanelContext";
import { SidebarToggle } from "components/icons/SidebarToggle";
import { Logo } from "components/logo/Logo";
import { useHasEnteredNewMainRoute } from "components/navigation/hooks/useHasEnteredNewMainRoute";
import { useMainMenuItems } from "components/navigation/navigation-panel/main-navigation-panel/navigation-icons-menu/useMainMenuItems";
import {
    ASSET_MANAGEMENT_ROUTE_BMP,
    CONNECTIONS_ROUTE,
    DASHBOARD_ROUTE,
    detectApplicationFromPath,
    HOME_ROUTE,
    PRODUCTS_ROUTE,
    REPORTS_ROUTE,
    RouteDefinition,
    SETTINGS_ROUTE,
    SUPPORT_ROUTE,
} from "components/router/Routes";
import { Breakpoint } from "domain/breakpoints";
import { FLAG_ASSET_MANAGEMENT, FLAG_RESTRUCTURED_NAVIGATION } from "services/feature/FeatureFlagService";
import { useIsAiHomeEnabled } from "services/feature/useIsAiHomeEnabled";
import { tenantHasFeatureLicense } from "services/tenants/tenantCookieService";
import { StoreState } from "store";
import { Application, setApplication } from "store/application";
import { setMobileMainNavigationOpen } from "store/mobileNavigation";
import { resetChildRoutePaths, toggleChildRoutePath } from "store/settingsMenu";
import { resolveColor } from "utils/commonFunctions";

import testIds from "testIds.json";

function getNavigationHeaderBadge(
    route: RouteDefinition,
    isHomeAiBadgeVisible: boolean
): NavigationHeaderBadge | undefined {
    const badgesByRoutePath = new Map<string, NavigationHeaderBadge>([
        [
            HOME_ROUTE.path,
            {
                variant: "New",
                isVisible: isHomeAiBadgeVisible,
            },
        ],
        [
            ASSET_MANAGEMENT_ROUTE_BMP.path,
            {
                variant: "New",
                isVisible: true,
            },
        ],
    ]);

    return badgesByRoutePath.get(route.path);
}

interface Props {
    onSubNavigationOpenChange: (value: boolean) => void;
    isSubNavigationOpened: boolean;
    isTrialTenant?: boolean;
    isDarkMode: boolean;
}

const SubNavigationPanel = (props: Props) => {
    const dispatch = useDispatch();
    const application = useSelector((state: StoreState) => state.applicationReducer.selected);
    const { pathname } = useLocation();
    const isAssetManagementFlagActive = useFeature(FLAG_ASSET_MANAGEMENT);
    const isRestructuredNavPanelActive = useFeature(FLAG_RESTRUCTURED_NAVIGATION);
    const isAiHomeEnabled = useIsAiHomeEnabled();
    const { conversationManagementHandlers } = useAIPanelContext();
    const subNavigationPanelRef = React.useRef<HTMLDivElement>(null);
    const subNavigationScrollContainerRef = React.useRef<HTMLDivElement>(null);
    const isFirstRender = React.useRef(true);
    const [isKebabMenuOpen, setIsKebabMenuOpen] = React.useState(false);
    const openSettingsChildRoutePaths = useSelector(
        (state: StoreState) => state.settingsMenuReducer.openChildRoutePaths
    );

    function isBamAvailable() {
        const hasFeatureLicense = tenantHasFeatureLicense("FEATURE_ASSET_MANAGEMENT");
        return isAssetManagementFlagActive && hasFeatureLicense;
    }

    function isOnBam() {
        return application === Application.BAM;
    }

    React.useEffect(() => {
        const currentApplication = detectApplicationFromPath(pathname);
        if (currentApplication !== undefined && currentApplication !== application) {
            if (currentApplication === Application.BAM && !isBamAvailable()) {
                return;
            }
            // In BMP+assets mode the asset routes are embedded in BMP navigation,
            // so navigating to /assets/* should not switch the app to BAM.
            if (
                currentApplication === Application.BAM &&
                application === Application.BMP &&
                isAssetManagementFlagActive
            ) {
                return;
            }
            dispatch(setApplication(currentApplication));
        }
    }, [pathname, isAssetManagementFlagActive]);

    React.useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (props.isSubNavigationOpened && subNavigationPanelRef.current) {
            const firstFocusable = subNavigationPanelRef.current.querySelector<HTMLElement>("button:not([disabled])");
            firstFocusable?.focus();
        }
    }, [props.isSubNavigationOpened]);

    React.useEffect(() => {
        const scrollContainer = subNavigationScrollContainerRef.current;
        if (!scrollContainer || !isKebabMenuOpen) {
            return;
        }

        const blockWheel = (event: WheelEvent) => {
            event.preventDefault();
        };

        const closeKebabOnScroll = () => {
            document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true }));
        };

        scrollContainer.addEventListener("wheel", blockWheel, { passive: false });
        scrollContainer.addEventListener("scroll", closeKebabOnScroll);

        return () => {
            scrollContainer.removeEventListener("wheel", blockWheel);
            scrollContainer.removeEventListener("scroll", closeKebabOnScroll);
        };
    }, [isKebabMenuOpen]);

    const handleLinkClick = (event: React.MouseEvent) => {
        const target = event.target as HTMLElement;
        const link = target.closest("a");
        // Conversations don't have routes, so the history rows and new conversation button are
        // matched by test ID instead.
        const conversationHistoryLink = target.closest(
            `[data-testid="${testIds.workArea.aiPanel.historyPanel.historyLink}"]`
        );
        const newConversationButton = target.closest(
            `[data-testid="${testIds.workArea.aiPanel.historyPanel.createNewButton}"]`
        );
        if ((link || conversationHistoryLink || newConversationButton) && window.innerWidth <= Breakpoint.MD) {
            props.onSubNavigationOpenChange(false);
            dispatch(setMobileMainNavigationOpen(false));
        }
    };

    const { activeMainMenuItem } = useMainMenuItems();
    const hasEnteredNewMainRoute = useHasEnteredNewMainRoute(activeMainMenuItem?.mainRoute);

    function renderSubNavigation() {
        switch (activeMainMenuItem?.mainRoute) {
            case DASHBOARD_ROUTE:
                return <DashboardSubmenu mainRoute={DASHBOARD_ROUTE} />;
            case PRODUCTS_ROUTE:
                return <ProductsSubmenu mainRoute={PRODUCTS_ROUTE} />;
            case CONNECTIONS_ROUTE:
                return <ConnectionsSubmenu mainRoute={CONNECTIONS_ROUTE} />;
            case HOME_ROUTE:
                return <HomeSubmenu mainRoute={HOME_ROUTE} />;
            case REPORTS_ROUTE:
                return <ReportsSubmenu mainRoute={REPORTS_ROUTE} enteredFromMainNavigation={hasEnteredNewMainRoute} />;
            case SETTINGS_ROUTE:
                return (
                    <SettingsSubmenu
                        mainRoute={SETTINGS_ROUTE}
                        enteredFromMainNavigation={hasEnteredNewMainRoute}
                        openChildRoutePaths={openSettingsChildRoutePaths}
                        resetChildRoutePaths={() => dispatch(resetChildRoutePaths())}
                        onToggleChildRoute={(path) => dispatch(toggleChildRoutePath(path))}
                    />
                );
            case SUPPORT_ROUTE:
                return <SupportSubmenu mainRoute={SUPPORT_ROUTE} />;
            case ASSET_MANAGEMENT_ROUTE_BMP:
                return (
                    <AssetsSubmenu
                        mainRoute={ASSET_MANAGEMENT_ROUTE_BMP}
                        enteredFromMainNavigation={hasEnteredNewMainRoute}
                        onKebabMenuOpenChange={setIsKebabMenuOpen}
                    />
                );
            default:
                return <NavigationSubMenu mainRoute={activeMainMenuItem?.mainRoute} />;
        }
    }

    return (
        <Box
            columnLayout
            className={classNames(style.subNavigationPanel, {
                [style.mobileOpen]: props.isSubNavigationOpened,
            })}
            onClick={handleLinkClick}
            ref={subNavigationPanelRef}
        >
            <Box verticalAlignment="Center" className={style.subNavigationPanelHeader}>
                <Logo isWhiteLogo={props.isDarkMode} />

                <IconButton
                    size="Md"
                    onClick={() => props.onSubNavigationOpenChange(false)}
                    data-testid={testIds.navigation.togglePanel.button}
                >
                    <SidebarToggle
                        color={
                            isOnBam()
                                ? resolveColor("--app-navigationPanel-iconColor")
                                : isRestructuredNavPanelActive
                                  ? resolveColor("--app-theme-brand-blue")
                                  : resolveColor("--theme-icon-fill-white")
                        }
                        rotate={false}
                    />
                </IconButton>
            </Box>

            {activeMainMenuItem && (
                <Box columnLayout className={style.subNavigationPanelContent} ref={subNavigationScrollContainerRef}>
                    <NavigationHeader
                        text={t(activeMainMenuItem.title)}
                        badge={getNavigationHeaderBadge(
                            activeMainMenuItem.mainRoute,
                            isAiHomeEnabled && !!conversationManagementHandlers
                        )}
                    />
                    <Box columnLayout className={style.subNavigationItemsContainer}>
                        {renderSubNavigation()}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default SubNavigationPanel;
